import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../auth/AuthContext';
import { API } from '../../api/api';
import { ChatDots, DashLg, Send } from 'react-bootstrap-icons';
import ReactMarkdown from 'react-markdown';
import c from './ChatWidget.module.css';

const ROUTE_LABELS = {
    '/available': 'Available Tests page',
    '/newTest': 'New Test page',
    '/performed': 'Performed Tests page',
    '/checkValidate': 'Choose Test to Validate page',
    '/validationResult': 'Validation Results page',
    '/predict': 'Predict Validation page',
    '/personalInformation': 'Personal Information page',
    '/invoices': 'Payments page',
    '/termsOfUse': 'Terms of Use page',
};

const PAGE_WELCOME = {
    '/available':        'Hi! I can see you\'re browsing the Available Tests. Would you like help choosing which assays to run for your compound?',
    '/newTest':          'Hi! Ready to run a new test? Tell me about your compound and I can suggest which assays make the most sense for your goals.',
    '/performed':        'Hi! I can see your performed tests. Would you like me to help you understand any of your results?',
    '/validationResult': 'Hi! Your validation results are here. Want me to walk you through what the scores mean?',
    '/predict':          'Hi! I\'m here to help interpret your predictions. Share a result and I\'ll explain what it means for your compound\'s safety profile.',
    '/checkValidate':    'Hi! Need help choosing which test to validate? I can explain the difference between assays and what each one is used for.',
    '/invoices':         'Hi! Any questions about your plan or available packages? I can explain what\'s included in each tier.',
};

const DEFAULT_WELCOME = 'Hi! I\'m the Toxometris assistant. I can help you understand toxicity predictions, navigate the platform, or answer questions about your results. What would you like to know?';

const IDLE_NUDGES = {
    '/available':        'Still deciding which assays to run? I can recommend a panel based on your compound type or development stage.',
    '/newTest':          'Need help with your SMILES input or unsure which endpoints to select? Just ask!',
    '/performed':        'Want me to summarize any of your previous results or compare compounds?',
    '/validationResult': 'Any of those results look unexpected? I can help explain what\'s driving the prediction.',
    '/predict':          'I\'m here if you\'d like a plain-language explanation of any of these scores.',
};

const DEFAULT_NUDGE = 'Still here if you have questions! I can explain any assay result, help you choose tests, or answer questions about the platform.';

const IDLE_DELAY_MS = 60000;

export default function ChatWidget() {
    const location = useLocation();
    const auth = useContext(AuthContext);

    const welcomeText = PAGE_WELCOME[location.pathname] || DEFAULT_WELCOME;

    const [open, setOpen] = useState(true);
    const [messages, setMessages] = useState([{ role: 'assistant', text: welcomeText }]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);
    const idleTimer = useRef(null);
    const nudgeSent = useRef(false);
    const userHasReplied = useRef(false);

    // On route change: if conversation hasn't started, update welcome message.
    // If the user has already engaged, keep the history and add a subtle page-change note.
    useEffect(() => {
        setMessages(prev => {
            const conversationStarted = prev.some(m => m.role === 'user');
            if (!conversationStarted) {
                // No real conversation yet — just swap the welcome message
                nudgeSent.current = false;
                userHasReplied.current = false;
                return [{ role: 'assistant', text: PAGE_WELCOME[location.pathname] || DEFAULT_WELCOME }];
            }
            // Conversation is ongoing — append a subtle navigation note so the AI has context
            const label = ROUTE_LABELS[location.pathname];
            if (!label) return prev;
            nudgeSent.current = false;
            return [...prev, { role: 'system', text: `📍 ${label}` }];
        });
    }, [location.pathname]);

    useEffect(() => {
        if (open) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            inputRef.current?.focus();
        }
    }, [messages, open]);

    const resetIdleTimer = useCallback(() => {
        clearTimeout(idleTimer.current);
        if (nudgeSent.current || userHasReplied.current) return;
        idleTimer.current = setTimeout(() => {
            if (!nudgeSent.current && !userHasReplied.current) {
                nudgeSent.current = true;
                const nudge = IDLE_NUDGES[location.pathname] || DEFAULT_NUDGE;
                setMessages(prev => [...prev, { role: 'assistant', text: nudge }]);
                setOpen(true);
            }
        }, IDLE_DELAY_MS);
    }, [location.pathname]);

    // Start idle timer when component mounts or route changes
    useEffect(() => {
        resetIdleTimer();
        return () => clearTimeout(idleTimer.current);
    }, [resetIdleTimer]);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        userHasReplied.current = true;
        clearTimeout(idleTimer.current);

        const userMsg = { role: 'user', text };
        const nextMessages = [...messages, userMsg];
        setMessages(nextMessages);
        setInput('');
        setLoading(true);

        try {
            const token = localStorage.getItem('access_token_toxometris');
            const response = await fetch(`${API.BASE_URL}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    message: text,
                    context: {
                        page: ROUTE_LABELS[location.pathname] || location.pathname,
                        account_name: auth?.account_name,
                    },
                    history: nextMessages
                        .filter(m => m.role !== 'system')
                        .slice(-10)
                        .map(m => ({ role: m.role, content: m.text })),
                }),
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const data = await response.json();

            // The backend answers HTTP 200 with the real code in the body - the
            // convention the whole portal uses - so `response.ok` is true even when
            // the request failed. It was the only check here, and `data.reply` is
            // null on failure, so a dead Anthropic key rendered an EMPTY assistant
            // bubble: no error, no hint, nothing to report. That is exactly what a
            // placeholder API key on dev looked like.
            if (data?.status === 'error' || !data?.reply) {
                // The body's own message can be an upstream error string - "401
                // invalid x-api-key" - which is for the log, not for a customer.
                console.warn('chat failed:', data?.status_code, data?.message);
                throw new Error(data?.message || 'No reply');
            }

            setMessages(prev => [...prev, { role: 'assistant', text: data.reply }]);
        } catch (err) {
            console.warn('chat error:', err);
            setMessages(prev => [
                ...prev,
                { role: 'assistant', text: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.' },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className={c.chatWidgetRoot}>
            {open && (
                <div className={c.chatPanel}>
                    <div className={c.chatHeader}>
                        <span className={c.chatHeaderTitle}>Toxometris Assistant</span>
                        <button className={c.closeBtn} onClick={() => setOpen(false)} aria-label="Minimize chat">
                            <DashLg size={20} />
                        </button>
                    </div>

                    <div className={c.chatMessages}>
                        {messages.map((msg, i) => (
                            <div
                                key={i}
                                className={
                                    msg.role === 'user' ? c.userBubble :
                                    msg.role === 'system' ? c.navNote :
                                    c.assistantBubble
                                }
                            >
                                {msg.role === 'assistant' ? (
                                    <div className={c.markdown}>
                                        <ReactMarkdown
                                            components={{
                                                a: ({ node, children, ...props }) => (
                                                    <a {...props} target="_blank" rel="noreferrer">{children}</a>
                                                ),
                                            }}
                                        >
                                            {msg.text}
                                        </ReactMarkdown>
                                    </div>
                                ) : (
                                    msg.text
                                )}
                            </div>
                        ))}
                        {loading && (
                            <div className={c.assistantBubble}>
                                <span className={c.typingDots}>
                                    <span /><span /><span />
                                </span>
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    <div className={c.chatInputRow}>
                        <textarea
                            ref={inputRef}
                            className={c.chatInput}
                            rows={1}
                            placeholder="Ask me anything..."
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={loading}
                        />
                        <button
                            className={c.sendBtn}
                            onClick={sendMessage}
                            disabled={!input.trim() || loading}
                            aria-label="Send message"
                        >
                            <Send size={16} />
                        </button>
                    </div>
                </div>
            )}

            <button
                className={c.fab}
                onClick={() => setOpen(prev => !prev)}
                aria-label="Open chat assistant"
            >
                <ChatDots size={26} />
            </button>
        </div>
    );
}
